// client/introspection.ts
import { logger } from "@adornis/base/logging.js";
import {
  getIntrospectionQuery
} from "graphql";
import { getTypeString } from "../baseqlTypes.js";
import { AdornisEntity } from "../entities/adornisEntity.js";
import { addDynamicEntity, addDynamicField, resetDynamicMetadata } from "../metadata/dynamic.js";
import { baseqlMetaData } from "../metadata/metaDataStore.js";
import { registerEnum } from "../metadata/register.js";
import { BaseQLClient } from "./baseqlClient.js";
var introspectServer = async () => {
  const handler = await BaseQLClient.getTransport();
  const introspectionResult = await handler.query(
    {
      name: "__schema",
      options: {},
      params: [],
      middlewares: [],
      arrayDepth: () => 0,
      type: () => AdornisEntity
    },
    { query: getIntrospectionQuery(), variables: {}, operationName: "IntrospectionQuery" }
  );
  return introspectionResult.data.__schema;
};
var resolveNodeWithoutError = (node) => {
  try {
    return baseqlMetaData.entities.resolveNode(node);
  } catch (error) {
  }
};
var makeBaseQLFieldDefinition = (fieldName, fieldType) => {
  let arrayDepth = 0;
  while (fieldType.kind === "LIST" || fieldType.kind === "NON_NULL") {
    if (fieldType.kind === "LIST")
      arrayDepth++;
    fieldType = fieldType.ofType;
  }
  let bqlType;
  if (fieldType.kind === "SCALAR") {
    bqlType = baseqlMetaData.scalars[fieldType.name]?.type;
  }
  if (fieldType.kind === "INTERFACE" || fieldType.kind === "OBJECT") {
    const bqlTypeNode = baseqlMetaData.entities.resolveNode(
      fieldType.kind === "INTERFACE" ? fieldType.name.slice(0, -9) : fieldType.name
    );
    bqlType = bqlTypeNode.type();
  }
  if (fieldType.kind === "UNION") {
    bqlType = baseqlMetaData.unions[fieldType.name]?.type;
  }
  if (fieldType.kind === "ENUM") {
    bqlType = baseqlMetaData.enums[fieldType.name]?.enum;
  }
  if (!bqlType)
    throw new Error(`could not find baseqlType for ${fieldName}, ${JSON.stringify(fieldType, null, 2)}`);
  return {
    name: fieldName,
    arrayDepth: () => arrayDepth,
    type: () => bqlType,
    options: {},
    params: [],
    middlewares: [],
    dynamic: true
  };
};
var updateDynamicMetadata = async () => {
  const introspectionResult = await introspectServer();
  resetDynamicMetadata();
  const dynamicNodes = [];
  const typeOrder = ["SCALAR", "ENUM", "INPUT_OBJECT", "OBJECT", "INTERFACE"];
  for (const type of typeOrder) {
    for (const nodeType of introspectionResult.types.filter((nodeType2) => nodeType2.kind === type)) {
      if (nodeType.kind === "OBJECT" && !nodeType.name.startsWith("__") && !["Query", "Mutation", "Subscription"].includes(nodeType.name)) {
        const existingNode = resolveNodeWithoutError(nodeType.name);
        if (!existingNode) {
          dynamicNodes.push(nodeType);
          continue;
        }
        for (const field of nodeType.fields) {
          if (existingNode.fields[field.name])
            continue;
          logger.debug({ name: field.name, type: field.type }, "adding dynamic field");
          addDynamicField(existingNode, makeBaseQLFieldDefinition(field.name, field.type));
        }
      }
      if (nodeType.kind === "ENUM" && !nodeType.name.startsWith("__") && !["Query", "Mutation", "Subscription"].includes(nodeType.name)) {
        const existingNode = baseqlMetaData.enums[nodeType.name];
        if (!existingNode) {
          registerEnum({
            enum: nodeType.enumValues.reduce((acc, curr) => ({ ...acc, [curr.name]: curr.name }), {}),
            name: nodeType.name,
            dynamic: true
          });
          continue;
        }
      }
    }
  }
  for (const newType of dynamicNodes) {
    let parentNode = baseqlMetaData.entities.root;
    if (newType.interfaces.length) {
      let inter = newType.interfaces[0]?.name.slice(0, -9) === newType.name ? newType.interfaces[1] : newType.interfaces[0];
      if (!inter)
        throw new Error("no interface found");
      parentNode = baseqlMetaData.entities.resolveNode(inter.name.slice(0, -9));
    }
    logger.debug({ type: getTypeString(parentNode.type), name: newType.name }, "adding dynamic node");
    const StubClass = class extends parentNode.type() {
      static {
        this._class = newType.name;
      }
    };
    addDynamicEntity(
      StubClass,
      Object.fromEntries(
        newType.fields.map((field) => {
          const fieldDef = makeBaseQLFieldDefinition(field.name, field.type);
          logger.debug(
            { name: fieldDef.name, type: fieldDef.type, arrayDepth: fieldDef.arrayDepth() },
            "adding dynamic field"
          );
          return [field.name, { type: () => wrapType(fieldDef.type, fieldDef.arrayDepth()), options: {} }];
        })
      ),
      {}
    );
  }
};
function wrapType(type, arrayDepth) {
  if (arrayDepth === 0)
    return type();
  return [wrapType(type, --arrayDepth)];
}
export {
  introspectServer,
  updateDynamicMetadata
};
